import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Subject, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    EditRequestAssigneePayload,
    EditRequestPriorityPayload,
    EditRequestStatusPayload,
} from './cereri-detalii/actualizare-cerere/actualizare-cerere.interface';
import {
    extraActionCererePayload,
    IInchidereLucrare,
} from './cereri.interface';
import { IAddCererePayload } from './add-cerere/add-cerere.interface';

@Injectable({
    providedIn: 'root',
})
export class CereriService {
    private token!: string;
    private httpOptions: any;

    private basePath = environment.interop.basePath;
    private getCereriListDTURL =
        this.basePath + environment.interop.deps.cerere.listDT;
    private getCerereURL =
        this.basePath + environment.interop.deps.cerere.find;
    private addCerereURL =
        this.basePath + environment.interop.deps.cerere.store;
    private getOperatoriByCompartimentUrl =
        this.basePath + environment.interop.deps.nomenclatoare.operatori.getOperatoriByCompartiment;

    // Mesagerie
    private sablonContent = new Subject<string>();
    sablonContent$ = this.sablonContent.asObservable();
    private postMessageURL =
        this.basePath + environment.interop.deps.cerere.postMessage;
    private rePostMessageURL =
        this.basePath + environment.interop.deps.cerere.rePostMessage;
    private fileUploadUrl =
        this.basePath + environment.interop.deps.cerere.file.fileUpload;
    private fileUploadBase64Url =
        this.basePath + environment.interop.deps.cerere.file.fileUploadBase64;

    // Actualizare cerere
    private updateStatusURL =
        this.basePath + environment.interop.deps.cerere.updateStatus;
    private updatePriorityURL =
        this.basePath + environment.interop.deps.cerere.updatePriority;
    private updateAssigneeURL =
        this.basePath + environment.interop.deps.cerere.updateAssignee;

    // Actiuni cerere
    private validateCerereUrl =
        this.basePath + environment.interop.deps.cerere.validateCerere;
    private invalidateCerereUrl =
        this.basePath + environment.interop.deps.cerere.invalidateCerere;
    private anulareCerereUrl =
        this.basePath + environment.interop.deps.cerere.anulareCerere;
    private finalizareCerereUrl =
        this.basePath + environment.interop.deps.cerere.finalizareCerere;
    private preluarePersonalaUrl =
        this.basePath + environment.interop.deps.cerere.preluarePersonala;
    private preluareNumarRegistraturaUrl =
        this.basePath +
        environment.interop.deps.cerere.preluareNumarRegistratura;

    private resetValidareCerereUrl =
        this.basePath + environment.interop.deps.cerere.resetValidareCerere;
    private resetInvalidareCerereUrl =
        this.basePath + environment.interop.deps.cerere.resetInvalidareCerere;
    private resetClasareCerereUrl =
        this.basePath + environment.interop.deps.cerere.resetClasareCerere;
    private resetInchidereCerereUrl =
        this.basePath + environment.interop.deps.cerere.resetInchidereCerere;

    // Altele
    public downloadDocumentUrl = this.basePath +
        environment.interop.deps.cerere.downloadDocument;

    constructor(private http: HttpClient) { }

    getCerere(id_cerere: number) {
        return this.http.get(this.getCerereURL + id_cerere);
    }

    addCerere(data: IAddCererePayload) {
        return this.http.post(this.addCerereURL, data);
    }

    getOperatoriByCompartiment(id_compartiment: number) {
        return this.http
            .get<{ errors: boolean; data: any[]; status_code: number }>(
                this.getOperatoriByCompartimentUrl + id_compartiment
            )
            .pipe(
                map((response) =>
                    response.data.map((item) => ({
                        id: item.id_user,
                        name: item.name,
                    }))
                ),
                catchError((error) => {
                    console.error('Error in get operatori by compartiment:', error);
                    return throwError(error);
                })
            );
    }

    // Mesagerie
    sendContent(content: string) {
        this.sablonContent.next(content);
    }

    fileUpload(formData: FormData) {
        return this.http.post<{ data: any }>(this.fileUploadUrl, formData);
    }

    postMessage(id: number, message: any) {
        return this.http.post(this.postMessageURL + id, message, this.httpOptions);
    }

    rePostMessage(id: number) {
        return this.http.post(this.rePostMessageURL + id, this.httpOptions);
    }

    // Actualizare cerere
    updateRequestStatus(requestId: number, status: EditRequestStatusPayload) {
        return this.http.put(
            this.updateStatusURL + requestId,
            status,
            this.httpOptions
        );
    }

    updateRequestPriority(requestId: number, priority: EditRequestPriorityPayload) {
        return this.http.put(
            this.updatePriorityURL + requestId,
            priority,
            this.httpOptions
        );
    }

    updateRequestAssignee(requestId: number, data: EditRequestAssigneePayload) {
        return this.http.put(
            this.updateAssigneeURL + requestId,
            data,
            this.httpOptions
        );
    }

    // Actiuni cerere
    validateCerere(cerereId: number) {
        return this.http.post(this.validateCerereUrl + cerereId, this.httpOptions);
    }

    invalidateCerere(cerereId: number, data: extraActionCererePayload) {
        return this.http.post(
            this.invalidateCerereUrl + cerereId,
            data,
            this.httpOptions
        );
    }

    anulareCerere(cerereId: number, data: extraActionCererePayload) {
        return this.http.post(
            this.anulareCerereUrl + cerereId,
            data,
            this.httpOptions
        );
    }

    finalizareCerere(cerereId: number, data: IInchidereLucrare) {
        return this.http.post(
            this.finalizareCerereUrl + cerereId,
            data,
            this.httpOptions
        );
    }

    preluarePersonala(cerereId: number) {
        return this.http.get(this.preluarePersonalaUrl + cerereId);
    }

    preluareNumarRegistratura(cerereId: number) {
        return this.http.put(this.preluareNumarRegistraturaUrl + cerereId, {});
    }

    resetValidareCerere(cerereId: number, data: any) {
        return this.http.put(this.resetValidareCerereUrl + cerereId, data);
    }

    resetInvalidareCerere(cerereId: number, data: any) {
        return this.http.put(this.resetInvalidareCerereUrl + cerereId, data);
    }

    resetClasareCerere(cerereId: number, data: any) {
        return this.http.put(this.resetClasareCerereUrl + cerereId, data);
    }

    resetInchidereCerere(cerereId: number, data: any) {
        return this.http.post(this.resetInchidereCerereUrl + cerereId, data);
    }

    // Altele


    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${this.token}`,
            }),
        };
    }
}